import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import type z from 'zod';

import {
    type StepProps,
    treatmentApproachesStepSchema,
    useUpdateOnboardingStepOptions,
} from '@/entities/practice-member';
import {
    notesSettingsOptions,
    useUpdateNotesSettingsOptions,
} from '@/entities/practice-member-settings';
import { treatmentApproachesOptions } from '@/entities/treatment-approach';
import { InlineMultiSelect } from '@/shared/ui/common/inline-multi-select';
import { CardFormSection } from '@/shared/ui/forms/card-form-section';

export function OnboardingTreatmentApproachesStep(props: StepProps) {
    const { data: notesSettings } = useSuspenseQuery(notesSettingsOptions());

    const form = useForm<z.infer<typeof treatmentApproachesStepSchema>>({
        mode: 'onChange',
        resolver: zodResolver(treatmentApproachesStepSchema),
        defaultValues: {
            treatmentApproaches: notesSettings.value.treatmentApproaches,
        },
    });
    const {
        formState: { isDirty, isSubmitting, errors },
    } = form;

    const mutation = useMutation(useUpdateNotesSettingsOptions());
    const updateOnboardingStepMutation = useMutation(useUpdateOnboardingStepOptions());

    const onSubmit = async (data: z.infer<typeof treatmentApproachesStepSchema>) => {
        try {
            if (props.nextStep) {
                updateOnboardingStepMutation.mutate(props.nextStep);
            }
            if (isDirty) {
                await mutation.mutateAsync(data);
            }
            props.onNext();
        } catch {
            console.error('Failed to update treatment approaches step');
        }
    };

    const handleManualSubmit = () => {
        form.handleSubmit(onSubmit)();
    };

    return (
        <FormProvider {...form}>
            <CardFormSection
                title="What treatment approaches do you typically use?"
                description="Select your most commonly used treatment approaches to personalize your notes"
                progressValue={props.progress}
                cardContentClassName="px-0 sm:px-4 overflow-hidden"
                errorMessage={errors.treatmentApproaches?.message}
                isFatalError={mutation.isError}
                disabledBackButton={isSubmitting}
                disabledNextButton={isSubmitting}
                onNext={handleManualSubmit}
                onBack={props.onBack}
            >
                <div className="max-w-[39.375rem] h-full sm:pt-3">
                    <Controller
                        control={form.control}
                        name="treatmentApproaches"
                        render={({ field }) => (
                            <InlineMultiSelect
                                badgeClassName="bg-card"
                                options={treatmentApproachesOptions}
                                selected={field.value}
                                onChange={field.onChange}
                                maxSelected={12}
                                disabled={isSubmitting}
                            />
                        )}
                    />
                </div>
            </CardFormSection>
        </FormProvider>
    );
}
